import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, CalendarClock, MapPin, Baby, User, Plus, X, Compass } from "lucide-react";
import { PillButton } from "../../components/ui/PillButton";
import { StickerCard } from "../../components/ui/StickerCard";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { TicketGate } from "../../components/ui/TicketGate";
import { EmptyState } from "../../components/ui/EmptyState";
import { CloudsLayer } from "../../components/ui/Decorations";
import { useApp } from "../../context/AppContext";
import { EVENT } from "../../data/mockData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

export default function MyBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getTicket, validateTicket, activities, getActivity, getSlot, slotRemaining, bookActivity, cancelBooking } = useApp();

  const initialCode = location.state?.code || sessionStorage.getItem("bltr_active_ticket") || "";
  const [code, setCode] = useState(() => (validateTicket(initialCode).ok ? initialCode : ""));
  const [dialogActivity, setDialogActivity] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const ticket = getTicket(code);

  useEffect(() => {
    if (ticket && location.state?.openActivity) {
      const a = getActivity(location.state.openActivity);
      if (a?.bookingRequired) openDialog(a);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  const openDialog = (activity) => { setSelectedSlot(null); setDialogActivity(activity); };

  const confirmBooking = () => {
    if (!selectedSlot) { toast.error("Pilih slot waktu dulu ya."); return; }
    const res = bookActivity(ticket.ticketCode, dialogActivity.id, selectedSlot);
    if (!res.ok) { toast.error(res.error); return; }
    setDialogActivity(null);
    navigate("/booking-confirmation", {
      state: { code: ticket.ticketCode, ref: res.ref, activityId: dialogActivity.id, slotId: selectedSlot },
    });
  };

  const bookedActivities = useMemo(() => {
    if (!ticket) return [];
    return ticket.bookings.filter((b) => b.status === "Confirmed").map((b) => ({
      ...b, activity: getActivity(b.activityId), slot: getSlot(b.activityId, b.slotId),
    }));
  }, [ticket, getActivity, getSlot]);

  const availableToBook = useMemo(() => {
    if (!ticket) return [];
    const bookedIds = ticket.bookings.filter((b) => b.status === "Confirmed").map((b) => b.activityId);
    return activities.filter((a) => a.bookingRequired && !bookedIds.includes(a.id));
  }, [ticket, activities]);

  if (!ticket) {
    return (
      <div className="relative pt-8 pb-16">
        <CloudsLayer />
        <div className="relative z-10 pt-4">
          <TicketGate
            title="My Booking"
            subtitle="Masukkan kode tiket untuk melihat & mengelola aktivitasmu."
            onValid={(t) => { sessionStorage.setItem("bltr_active_ticket", t.ticketCode); setCode(t.ticketCode); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-sea-deep" data-testid="mybooking-welcome">
            Halo, {ticket.parentName}! 👋
          </h1>
          <p className="text-sea-deep/60 font-semibold">Kelola tiket & aktivitas keluargamu di sini.</p>
        </div>
        <button
          onClick={() => { sessionStorage.removeItem("bltr_active_ticket"); setCode(""); }}
          className="flex items-center gap-1 text-sea-deep/50 font-bold text-sm bg-white rounded-full px-4 py-2 shadow-playful"
          data-testid="mybooking-logout"
        >
          <LogOut className="w-4 h-4" /> Ganti Tiket
        </button>
      </div>

      {/* Ticket info */}
      <StickerCard className="mt-6 p-6" data-testid="mybooking-ticket-info">
        <div className="grid sm:grid-cols-4 gap-4">
          <Info label="Kode Tiket" value={ticket.ticketCode} highlight />
          <Info label="Orang Tua" value={ticket.parentName} icon={<User className="w-3.5 h-3.5" />} />
          <Info label="Anak" value={`${ticket.childName}${ticket.childAge ? ` (${ticket.childAge}th)` : ""}`} icon={<Baby className="w-3.5 h-3.5" />} />
          <div>
            <p className="text-xs uppercase font-bold text-sea-deep/50">Status Tiket</p>
            <div className="mt-1"><StatusBadge status={ticket.paymentStatus} /></div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 text-sm font-bold text-sea-deep/70 flex flex-wrap gap-4">
          <span className="flex items-center gap-1"><CalendarClock className="w-4 h-4 text-coral" /> {EVENT.dateLabel}</span>
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-coral" /> {EVENT.venue}</span>
        </div>
      </StickerCard>

      {/* Your Activities */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-2xl text-sea-deep">Aktivitas Kamu</h2>
        <PillButton size="sm" variant="secondary" onClick={() => navigate("/activities")} data-testid="mybooking-explore">
          <Compass className="w-4 h-4" /> Jelajahi Aktivitas
        </PillButton>
      </div>

      {bookedActivities.length ? (
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {bookedActivities.map((b) => (
            <StickerCard key={b.ref} className="p-5 flex items-center gap-4" data-testid={`booked-${b.activityId}`}>
              <img src={b.activity.icon} alt="" className="w-14 h-14 object-contain shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg text-sea-deep truncate">{b.activity.name}</h3>
                <p className="text-sm font-bold text-sea-deep/60">{b.slot ? `${b.slot.start}–${b.slot.end}` : ""} · {b.activity.location}</p>
                <div className="mt-1 flex items-center gap-2">
                  <StatusBadge status={b.status} />
                  <span className="text-xs font-bold text-sea-deep/40">Ref: {b.ref}</span>
                </div>
              </div>
              <button
                onClick={() => { cancelBooking(ticket.ticketCode, b.ref); toast.success("Booking dibatalkan. Slot dikembalikan."); }}
                className="w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20"
                title="Batalkan"
                data-testid={`cancel-${b.ref}`}
              >
                <X className="w-5 h-5" />
              </button>
            </StickerCard>
          ))}
        </div>
      ) : (
        <EmptyState
          title="Belum ada aktivitas yang di-booking!"
          message="Petualangan BLTR-mu sudah menunggu. Yuk pilih aktivitas favorit keluarga."
          ctaLabel="Explore Activities"
          onCta={() => navigate("/activities")}
        />
      )}

      {/* Available to book */}
      {availableToBook.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-2xl text-sea-deep">Booking Aktivitas Lain</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableToBook.map((a) => {
              const rem = a.slots.reduce((s, sl) => s + slotRemaining(a.id, sl.id), 0);
              const full = rem <= 0;
              return (
                <StickerCard key={a.id} className="p-5" data-testid={`available-${a.slug}`}>
                  <div className="flex items-center gap-3">
                    <img src={a.icon} alt="" className="w-12 h-12 object-contain" />
                    <div>
                      <h3 className="font-display text-lg text-sea-deep">{a.name}</h3>
                      <p className={cn("text-xs font-bold", full ? "text-destructive" : "text-grass")}>{full ? "Fully Booked" : `${rem} slot tersisa`}</p>
                    </div>
                  </div>
                  <PillButton size="sm" className="w-full mt-3" disabled={full} onClick={() => openDialog(a)} data-testid={`book-${a.slug}`}>
                    <Plus className="w-4 h-4" /> Book Slot
                  </PillButton>
                </StickerCard>
              );
            })}
          </div>
        </div>
      )}

      {/* Slot selection dialog */}
      <Dialog open={!!dialogActivity} onOpenChange={(o) => !o && setDialogActivity(null)}>
        <DialogContent className="rounded-[2rem] border-4 border-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-sea-deep">{dialogActivity?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-semibold text-sea-deep/60">Minggu, {EVENT.dateLabel} · Pilih slot waktu:</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {dialogActivity?.slots.map((s) => {
              const rem = slotRemaining(dialogActivity.id, s.id);
              const full = rem <= 0;
              const active = selectedSlot === s.id;
              return (
                <button
                  key={s.id}
                  disabled={full}
                  onClick={() => setSelectedSlot(s.id)}
                  data-testid={`slot-${s.id}`}
                  className={cn(
                    "rounded-2xl border-2 p-3 text-center transition-colors",
                    full ? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed" :
                    active ? "border-coral bg-coral/10" : "border-slate-200 bg-white hover:border-sky-dark"
                  )}
                >
                  <p className="font-display text-sea-deep">{s.start}–{s.end}</p>
                  <p className={cn("text-xs font-bold mt-1", full ? "text-destructive" : "text-grass")}>{full ? "Penuh" : `${rem} spots left`}</p>
                </button>
              );
            })}
          </div>
          <PillButton className="w-full mt-4" onClick={confirmBooking} data-testid="confirm-slot-booking">
            Confirm Booking
          </PillButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value, highlight, icon }) => (
  <div>
    <p className="text-xs uppercase font-bold text-sea-deep/50 flex items-center gap-1">{icon}{label}</p>
    <p className={cn("mt-1 font-display truncate", highlight ? "text-coral text-lg tracking-wide" : "text-sea-deep")}>{value}</p>
  </div>
);
